import React, { useEffect, useState } from 'react';
import InventoryTable from './InventoryTable';
import { getInventory, importInventory } from '../../services/inventory';
import styled from '@emotion/styled';
import DotsLoading from '../ui/DotsLoading';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';

const Container = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  
  @media (max-width: 1200px) {
    gap: 16px;
  }
  
  @media (max-width: 768px) {
    gap: 12px;
    margin-bottom: 16px;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
    margin-bottom: 12px;
  }
`;

const Label = styled.div`
  color: #ededed;
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 1rem;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const SearchInput = styled.input`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 8px 12px;
  color: #ededed;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s ease;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    border-color: #F43F60;
  }
  
  @media (max-width: 768px) {
    padding: 6px 10px;
    font-size: 13px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 5px 8px;
    font-size: 12px;
    border-radius: 4px;
  }
`;

const Select = styled.select`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 8px 12px;
  color: #ededed;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s ease;
  cursor: pointer;
  
  &:focus {
    border-color: #F43F60;
  }
  
  option {
    background: #1A1A1A;
    color: #ededed;
  }
  
  @media (max-width: 768px) {
    padding: 6px 10px;
    font-size: 13px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 5px 8px;
    font-size: 12px;
    border-radius: 4px;
  }
`;

const StatsCard = styled.div`
  background: #25253C;
  border-radius: 10px;
  padding: 16px;
  
  @media (max-width: 768px) {
    padding: 12px;
    margin-bottom: 16px;
    border-radius: 8px;
  }
  
  @media (max-width: 480px) {
    padding: 10px;
    margin-bottom: 12px;
    border-radius: 6px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 12px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 10px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 8px;
  }
`;

const StatItem = styled.div`
  text-align: center; 
  border-right: 1px solid rgba(255, 255, 255, 0.10);

  :last-child { 
    border-right: none;
  }
  
  @media (max-width: 768px) {
    border-right: none;
  }
`;

const StatValue = styled.div`
  color: #fff;
  font-size: 30px;
  font-weight: 700;
  margin-bottom: 4px;
  
  @media (max-width: 1200px) {
    font-size: 24px;
  }
  
  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 2px;
  }
  

`;

const StatLabel = styled.div`
  color: #9E9E9E;
  font-size: 16px;

`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding: 16px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    margin-top: 16px;
    padding: 12px 0;
  }
  
  @media (max-width: 480px) {
    gap: 12px;
    margin-top: 12px;
    padding: 8px 0;
  }
`;

const PaginationInfo = styled.div`
  color: #9E9E9E;
  font-size: 14px;
  
  @media (max-width: 768px) {
    font-size: 14px;
    text-align: center;
  }
  
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 768px) {
    gap: 6px;
  }
  
  @media (max-width: 480px) {
    gap: 4px;
  }
`;

const PageButton = styled.button`
  background: ${props => props.active ? '#F43F60' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.active ? '#F43F60' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.active ? '#ffffff' : '#ededed'};
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: ${props => props.active ? '#E03A56' : 'rgba(255, 255, 255, 0.1)'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 6px 10px;
    font-size: 13px;
    border-radius: 4px;
  }
  
  @media (max-width: 480px) {
    padding: 4px 8px;
    font-size: 12px;
    border-radius: 3px;
  }
`;

const ImportButton = styled.button`
  background: #4ECDC4;
  border: 1px solid #4ECDC4;
  color: #ffffff;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover:not(:disabled) {
    background: #44A08D;
    border-color: #44A08D;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 13px;
    gap: 6px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 8px 12px;
    font-size: 12px;
    gap: 4px;
    border-radius: 4px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 12px;
  }
`;

const StatsSection = styled.div`
  display: grid;
  gap: 24px;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    gap: 16px;
    margin-bottom: 16px;
  }
  
  @media (max-width: 480px) {
    gap: 12px;
    margin-bottom: 12px;
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const StatsCardTitle = styled.div`
  padding-bottom: 12px; 
  margin-bottom: 15px;
  color: rgba(255, 255, 255, 0.50);
  font-size: 16px;
  text-transform: uppercase;
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    padding-bottom: 10px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 12px;
    padding-bottom: 8px;
  }
`;

const StatsCardHeader = styled.div`
  margin-bottom: 15px;
  color: rgba(255, 255, 255, 0.50);
  font-size: 16px;
  padding-bottom: 12px;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 768px) {
    gap: 6px;
  }
  
  @media (max-width: 480px) {
    gap: 4px;
  }
`;

const StatsCardAccent = styled.div`
  width: 4px;
  height: 20px;
  background: ${props => props.color || '#F43F60'};
  border-radius: 2px;
  
  @media (max-width: 768px) {
    height: 16px;
  }
  
  @media (max-width: 480px) {
    height: 14px;
  }
`;

const StatsItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background: #1E1E35;
  border-radius: 6px;
  
  @media (max-width: 768px) {
    padding: 6px 10px;
    border-radius: 4px;
  }
  
`;

const StatsItemLabel = styled.span`
  color: #9E9E9E;
  font-size: 16px;
  display: block;
  text-transform: capitalize;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
  
`;

const StatsItemValue = styled.span`
  color: #fff;
  font-size: 24px;
  font-weight: 600;

  
  @media (max-width: 768px) {
     font-size: 18px;
  }
  
`;

const PaginationControlsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    width: 100%;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const PerPageContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 768px) {
    gap: 6px;
  }
  
  @media (max-width: 480px) {
    gap: 4px;
  }
`;

const InventoryTableContainer = () => {
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        pages: 0
    });

    // Filter states
    const [filters, setFilters] = useState({
        make: '',
        model: '',
        year: '',
        minPrice: '',
        maxPrice: '',
        condition: 'all',
        status: 'all',
        page: 1,
        limit: 10
    });

    // Stats state
    const [stats, setStats] = useState({
        total: 0,
        new: 0,
        used: 0,
        transmissionTypes: {},
        driveTrainTypes: {},
        views: { total: 0, average: 0, max: 0, min: 0, totalVehicles: 0 }
    });

    // Import state
    const [importing, setImporting] = useState(false);

    useEffect(() => {
        const fetchInventory = async () => {
            setLoading(true);
            setError('');
            try {
                // Build query parameters
                const queryParams = new URLSearchParams();

                Object.entries(filters).forEach(([key, value]) => {
                    if (value && value !== 'all') {
                        queryParams.append(key, value);
                    }
                });

                const res = await getInventory(queryParams.toString());
                if (res.status === 'success') {
                    setVehicles(res.data.vehicles || []);
                    setPagination(res.data.pagination || {
                        total: 0,
                        page: 1,
                        pages: 0
                    });

                    // Use pre-calculated stats from API response
                    const statsData = {
                        total: res.data.pagination?.total || 0,
                        new: parseInt(res.data.condition?.new) || 0,
                        used: parseInt(res.data.condition?.used) || 0,
                        transmissionTypes: res.data.transmission || {},
                        driveTrainTypes: res.data.driveTrain || {},
                        views: res.data.views || { total: 0, average: 0, max: 0, min: 0, totalVehicles: 0 }
                    };
                    setStats(statsData);
                } else {
                    setError(res.message || 'Failed to fetch inventory');
                }
            } catch (err) {
                setError('Failed to fetch inventory');
                console.error('Error fetching inventory:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchInventory();
    }, [filters]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: 1 // Reset to first page when filters change
        }));
    };

    const handlePageChange = (page) => {
        setFilters(prev => ({
            ...prev,
            page
        }));
    };

    const handleImportFromMarketcheck = async () => {
        setImporting(true);
        try {
            const importData = {
                vehiclesLimit: 500,
                vehiclePageSize: 50
            };
            
            const response = await importInventory(importData);
            if (response.status === 'success') {
                // Refresh the inventory data after successful import
                const queryParams = new URLSearchParams();
                Object.entries(filters).forEach(([key, value]) => {
                    if (value && value !== 'all') {
                        queryParams.append(key, value);
                    }
                });
                
                const res = await getInventory(queryParams.toString());
                if (res.status === 'success') {
                    setVehicles(res.data.vehicles || []);
                    setPagination(res.data.pagination || {
                        total: 0,
                        page: 1,
                        pages: 0
                    });

                    // Use pre-calculated stats from API response
                    const statsData = {
                        total: res.data.pagination?.total || 0,
                        new: 0, // Will be calculated from condition data if available
                        used: res.data.condition?.used || 0,
                        transmissionTypes: res.data.transmission || {},
                        driveTrainTypes: res.data.driveTrain || {},
                        views: res.data.views || { total: 0, average: 0, max: 0, min: 0, totalVehicles: 0 }
                    };
                    setStats(statsData);
                }
            } else {
                console.error('Import failed:', response.message);
            }
        } catch (error) {
            console.error('Error importing from Marketcheck:', error);
        } finally {
            setImporting(false);
        }
    };

    return (
        <Container>
            <ButtonContainer>
                <ImportButton 
                    onClick={handleImportFromMarketcheck} 
                    disabled={importing}
                >
                    <Download size={16} />
                    {importing ? 'Importing...' : 'Import from Marketcheck'}
                </ImportButton>
            </ButtonContainer>
            
            <StatsSection>
                {/* Main Stats */}
                <StatsCard>
                    <StatsCardTitle>Inventory Overview</StatsCardTitle>
                    <StatsGrid>
                        <StatItem>
                            <StatValue>{stats.views?.totalVehicles?.toLocaleString()}</StatValue>
                            <StatLabel>Total Vehicles</StatLabel>
                        </StatItem>
                        <StatItem>
                            <StatValue>{stats.total - stats.used}</StatValue>
                            <StatLabel>New Vehicles</StatLabel>
                        </StatItem>
                        <StatItem>
                            <StatValue>{stats.used}</StatValue>
                            <StatLabel>Used Vehicles</StatLabel>
                        </StatItem>
                        <StatItem>
                            <StatValue>{stats.views?.total?.toLocaleString()}</StatValue>
                            <StatLabel>Total Views</StatLabel>
                        </StatItem>
                        <StatItem>
                            <StatValue>{stats.views?.average?.toFixed(1)}</StatValue>
                            <StatLabel>Avg Views</StatLabel>
                        </StatItem>
                        <StatItem>
                            <StatValue>{stats.views?.max}</StatValue>
                            <StatLabel>Max Views</StatLabel>
                        </StatItem>
                        <StatItem>
                            <StatValue>{stats.views?.min}</StatValue>
                            <StatLabel>Min Views</StatLabel>
                        </StatItem>
                    </StatsGrid>
                </StatsCard>

                {/* Transmission & Drive Train Stats */}
                <StatsRow>
                    {/* Transmission Types */}
                    {Object.keys(stats.transmissionTypes || {}).length > 0 && (
                        <StatsCard>
                            <StatsCardHeader>
                                <StatsCardAccent color="#F43F60" />
                                Transmission Types
                            </StatsCardHeader>
                             <div style={{ display: 'flex', gap: '12px',flexWrap: 'wrap' }}>
                                {Object.entries(stats.transmissionTypes || {}).map(([type, count]) => (
                                    <StatsItem key={type}>
                                      <div style={{display: 'flex', alignItems: 'center', flexWrap: 'wrap'}}>
                                          <img className="custom-img-fluid" src={'/TransmissionTypes.png'} alt="TransmissionTypes" loading="lazy" style={{height: '40px', flexShrink: '0'}} />
                                          <div style={{marginLeft: '15px', paddingRight: '20px'}}>
                                            <StatsItemValue>{count}</StatsItemValue>
                                            <StatsItemLabel>{type}</StatsItemLabel>
                                          </div>
                                        </div>
                                    </StatsItem>
                                ))}
                            </div>
                        </StatsCard>
                    )}

                    {/* Drive Train Types */}
                    {Object.keys(stats.driveTrainTypes || {}).length > 0 && (
                        <StatsCard>
                            <StatsCardHeader>
                                <StatsCardAccent color="#4ECDC4" />
                                Drive Train Types
                            </StatsCardHeader>
                           <div style={{ display: 'flex', gap: '12px',flexWrap: 'wrap' }}>
                                {Object.entries(stats.driveTrainTypes || {}).map(([type, count]) => (
                                  <StatsItem key={type}> 
                                    <div style={{display: 'flex', alignItems: 'center', flexWrap: 'wrap'}}>
                                        <img className="custom-img-fluid" src={'/DriveTrainTypes.png'} alt="DriveTrainTypes" loading="lazy" style={{height: '40px', flexShrink: '0'}} />
                                        <div style={{marginLeft: '15px', paddingRight: '20px'}}>
                                          <StatsItemValue>{count}</StatsItemValue>
                                          <StatsItemLabel>{type}</StatsItemLabel>
                                        </div>
                                      </div>
                                    </StatsItem>
                                ))}
                            </div>
                        </StatsCard>
                    )}
                </StatsRow>
            </StatsSection>

            {loading ? (
                <DotsLoading />
            ) : error ? (
                <div style={{ color: '#F43F60', textAlign: 'center', margin: '40px 0' }}>{error}</div>
            ) : vehicles?.length <= 0 ? <div style={{ color: '#9E9E9E', textAlign: 'center', margin: '40px 0' }}>No vehicles found</div> : (
                <>
                    <InventoryTable vehicles={vehicles} height="52vh" />

                    {pagination.pages > 1 && (
                        <PaginationContainer>
                            <PaginationInfo>
                                Showing {((pagination.page - 1) * filters.limit) + 1} to {Math.min(pagination.page * filters.limit, pagination.total)} of {pagination.total} vehicles
                            </PaginationInfo>

                            <PaginationControlsWrapper>
                                <PerPageContainer>
                                    <Label style={{ fontSize: '14px', margin: 0 }}>Per Page:</Label>
                                    <Select
                                        value={filters.limit}
                                        onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                                        style={{ width: '80px' }}
                                    >
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </Select>
                                </PerPageContainer>

                                <PaginationControls>
                                    <PageButton
                                        onClick={() => handlePageChange(pagination.page - 1)}
                                        disabled={pagination.page <= 1}
                                    >
                                        <ChevronLeft size={16} />
                                    </PageButton>

                                    {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                                        const pageNum = i + 1;
                                        return (
                                            <PageButton
                                                key={pageNum}
                                                active={pageNum === pagination.page}
                                                onClick={() => handlePageChange(pageNum)}
                                            >
                                                {pageNum}
                                            </PageButton>
                                        );
                                    })}

                                    {pagination.pages > 5 && (
                                        <>
                                            {pagination.page > 3 && <span style={{ color: '#9E9E9E' }}>...</span>}
                                            {pagination.page > 3 && pagination.page < pagination.pages - 2 && (
                                                <PageButton
                                                    active={true}
                                                    onClick={() => handlePageChange(pagination.page)}
                                                >
                                                    {pagination.page}
                                                </PageButton>
                                            )}
                                            {pagination.page < pagination.pages - 2 && <span style={{ color: '#9E9E9E' }}>...</span>}
                                            {pagination.page < pagination.pages - 2 && (
                                                <PageButton
                                                    onClick={() => handlePageChange(pagination.pages)}
                                                >
                                                    {pagination.pages}
                                                </PageButton>
                                            )}
                                        </>
                                    )}

                                    <PageButton
                                        onClick={() => handlePageChange(pagination.page + 1)}
                                        disabled={pagination.page >= pagination.pages}
                                    >
                                        <ChevronRight size={16} />
                                    </PageButton>
                                </PaginationControls>
                            </PaginationControlsWrapper>
                        </PaginationContainer>
                    )}
                </>
            )
            }
        </Container>
    );
};

export default InventoryTableContainer; 